/**
 * The event view-model and pure presentation helpers shared by the activity
 * timeline components (TimelineEventCard / Row / Group / List and
 * ActivityTimeline). Each app maps its own API rows onto
 * `TimelineEventView`; nothing here fetches or knows an endpoint.
 */
import {
  Bell, CalendarDays, DoorOpen, EthernetPort, Key, Laptop, Lock, Network, Router, Settings, User, Users, Wifi, Zap,
  type LucideIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import type { GroupableEvent } from './groupEvents';

/** What produced the event — drives the emoji / icon. All parts optional. */
export interface EventKind {
  /** Producing app, e.g. `axema`, `doorbell`, `wisenet`. */
  app?: string | null;
  /** Producing model, e.g. `AxemaEvent`, `Wireless`. */
  model?: string | null;
  /** Event type, e.g. `door_open`, `alarm_off`, `ring`. */
  type?: string | null;
}

/** One activity event, ready to render. */
export interface TimelineEventView extends GroupableEvent {
  id: number;
  title: string;
  /** `info` | `success` | `warning` | `error` | `critical` (case-insensitive). */
  severity: string;
  /** When it happened — ISO string or Date. */
  time: string | Date;
  description?: string | null;
  /** Who did it. */
  actorName?: string | null;
  /** Where, most specific: the device (door, reader, camera). */
  deviceName?: string | null;
  /** Where, broader: floor / address / room label. */
  location?: string | null;
  kind?: EventKind;
  /** The event's dedup key (`NodusEvent.external_id`), e.g. `booking:42:booking_approved`. */
  externalId?: string | null;
}

const BOOKING_CHAIN = /^booking:(\d+):/;

/** `getChainKey` for `groupEvents`: every timeline event of one meeting-room booking shares one key (#401), so
 *  "Erik sent it for approval" and "Anna approved it" minutes later read as one entry. */
export function bookingChainKey(event: { externalId?: string | null }): string | null {
  const match = BOOKING_CHAIN.exec(event.externalId ?? '');
  return match ? `booking:${match[1]}` : null;
}

/** IoT location-state kinds that read as an alarm: the title carries the kind
 *  label (`Alarm: Armed — …`, `Pre-alarm: Active — …`, from `iot.services._emit_one`). */
const IOT_ALARM_LABEL = /^(alarm|pre-alarm)\b/i;

/** Emoji glyph for an event, used on the Timeline cards and rows. */
export function eventEmoji(kind?: EventKind | null, title?: string | null): string {
  const app = kind?.app?.toLowerCase() ?? '';
  const type = kind?.type?.toLowerCase() ?? '';

  if (app === 'iot' || app.includes('iot') || type.startsWith('iot.')) {
    // One event type for every location-state change (`iot.location_state_changed`);
    // the kind lives in the title's label, so an alarm must not fall through to 📋.
    if (IOT_ALARM_LABEL.test((title ?? '').trim())) return '🚨';
    return '📡';
  }

  if (app === 'axema' || app.includes('access')) {
    if (type.includes('door')) return '🚪';
    if (type.includes('alarm')) return '🚨';
    if (type.includes('tamper')) return '⚠️';
    if (type.includes('denied')) return '❌';
    if (type.includes('granted')) return '✅';
    if (type.includes('card')) return '💳';
    return '🔐';
  }

  if (app === 'doorbell' || app.includes('doorbell')) {
    if (type.includes('ring')) return '🔔';
    if (type.includes('call')) return '📞';
    if (type.includes('answered')) return '✅';
    if (type.includes('missed')) return '❌';
    return '🔔';
  }

  if (app === 'wisenet' || app.includes('camera')) return '📹';

  if (type.startsWith('booking_')) return '📅';

  return '📋';
}

/** Lucide icon for an event, used as the Recent-activity bullet. */
export function eventIcon(kind?: EventKind | null): LucideIcon {
  const model = kind?.model?.toLowerCase() || '';
  const type = kind?.type?.toLowerCase() || '';

  if (type.startsWith('booking_')) return CalendarDays;
  if (model.includes('door') || type.includes('door') || type.includes('bell')) return Bell;
  if (model.includes('wireless') || model.includes('wifi')) return Wifi;
  if (model.includes('outlet') || model.includes('interface')) return EthernetPort;
  if (model.includes('network') || model.includes('vlan')) return Network;
  if (model.includes('device') || model.includes('mikrotik') || model.includes('arista')) return Router;
  if (model.includes('client') || model.includes('unifi')) return Laptop;
  if (model.includes('user') || model.includes('person')) return User;
  if (model.includes('tenant')) return Users;
  if (model.includes('room') || model.includes('floor')) return DoorOpen;
  if (model.includes('access') || model.includes('key') || model.includes('axema')) return Key;
  if (type.includes('lock') || type.includes('unlock')) return Lock;
  if (type.includes('config') || type.includes('setting')) return Settings;
  return Zap;
}

/** Mantine colour for a severity (Recent-activity bullets). */
export const SEVERITY_COLOR: Record<string, string> = {
  info: 'blue',
  success: 'green',
  warning: 'yellow',
  error: 'red',
  critical: 'red',
};

/** Colour of the severity dot on Timeline cards. */
export const severityDotColor = (severity: string): string =>
  severity === 'error' || severity === 'critical' ? 'var(--mantine-color-red-6)' :
  severity === 'warning' ? 'var(--mantine-color-yellow-5)' :
  severity === 'success' ? 'var(--mantine-color-green-5)' :
  'var(--mantine-color-dimmed)';

/** Badge variant for the severity label on Timeline cards and rows. */
export const severityBadgeVariant = (severity: string): string => {
  switch (severity.toLowerCase()) {
    case 'error':
    case 'critical':
      return 'destructive';
    case 'warning':
      return 'secondary';
    case 'success':
      return 'default';
    default:
      return 'outline';
  }
};

/** `HH:mm:ss` clock time, in the viewer's local timezone. */
export const clockTime = (time: string | Date): string => format(new Date(time), 'HH:mm:ss');

/** `YYYY-MM-DD HH:mm:ss`, Swedish ISO-style — the full timestamp behind a single
 *  event's relative-time tooltip. Local timezone, like `clockTime`. */
export const formatFullDateTime = (time: string | Date): string => format(new Date(time), 'yyyy-MM-dd HH:mm:ss');

/** `HH:mm:ss–HH:mm:ss`, or a single `HH:mm:ss` when both ends fall in the same second. */
export const formatSpan = (start: Date, end: Date): string => {
  const a = format(start, 'HH:mm:ss');
  const b = format(end, 'HH:mm:ss');
  return a === b ? a : `${a}–${b}`;
};

/** `+15s` / `+1m 05s`: how long after the group's FIRST event a member happened.
 *  Reads the order and pacing of a burst better than a relative-to-now time
 *  repeated on every row (three Wi-Fi auths 15s apart all say "2 minutes ago"). */
export const formatOffset = (time: string | Date, groupStart: Date): string => {
  const deltaMs = Math.max(0, new Date(time).getTime() - groupStart.getTime());
  const totalSeconds = Math.round(deltaMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `+${minutes}m ${String(seconds).padStart(2, '0')}s` : `+${seconds}s`;
};
